import React, { useEffect, useState, useRef } from 'react';
import { CameraIcon, LoaderIcon, UploadIcon, Trash2Icon } from 'lucide-react';
import { db, storage } from '../../firebase';
import { doc, updateDoc, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../utils/imageUtils';
import toast from 'react-hot-toast';
interface StudentProfilePhotoUploadProps {
  uid: string;
  currentPhotoUrl: string | null;
  firstName: string;
  lastName: string;
  isDark: boolean;
  onPhotoUpdated: (newUrl: string | null) => void;
}
export const StudentProfilePhotoUpload: React.FC<StudentProfilePhotoUploadProps> = ({
  uid,
  currentPhotoUrl,
  firstName,
  lastName,
  isDark,
  onPhotoUpdated
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const getInitials = () => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${first}${last}` || '?';
  };
  /**
   * Sync profile image URL to all teacher student subcollection docs
   * where this student is linked. Non-blocking — failures are logged but don't affect UX.
   */
  const syncToTeacherStudentDocs = async (imageUrl: string | null) => {
    try {
      const studentsQuery = query(collectionGroup(db, 'students'), where('linkedUid', '==', uid));
      const snapshot = await getDocs(studentsQuery);
      const updatePromises = snapshot.docs.map(docSnap => updateDoc(docSnap.ref, {
        profileImageUrl: imageUrl
      }).catch(err => {
        console.warn('[ProfilePhoto] Failed to sync to teacher doc:', docSnap.ref.path, err);
      }));
      await Promise.all(updatePromises);
      console.log(`[ProfilePhoto] Synced profileImageUrl to ${snapshot.size} teacher student doc(s)`);
    } catch (error) {
      console.warn('[ProfilePhoto] Non-blocking sync to teacher docs failed:', error);
    }
  };
  // Core upload routine — accepts a File from either the input or drag-and-drop
  const uploadFile = async (file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a JPG, PNG, or WebP image');
      return;
    }
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    setIsUploading(true);
    setUploadProgress(0);
    try {
      // 1. Compress image
      let fileToUpload: File | Blob = file;
      try {
        const compressedBlob = await compressImage(file);
        fileToUpload = new File([compressedBlob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
      } catch (compressError) {
        console.warn('Image compression failed, using original file', compressError);
      }
      if (!isMounted.current) return;
      // 2. Upload to Firebase Storage
      const timestamp = new Date().getTime();
      const filename = `${timestamp}_${file.name.replace(/\s+/g, '_')}`;
      const storagePath = `profile-photos/${uid}/${filename}`;
      const storageRef = ref(storage, storagePath);
      const metadata = {
        contentType: fileToUpload.type,
        customMetadata: {
          uploadedBy: uid,
          originalFilename: file.name,
          userType: 'student'
        }
      };
      const uploadTask = uploadBytesResumable(storageRef, fileToUpload, metadata);
      await new Promise<void>((resolve, reject) => {
        uploadTask.on('state_changed', snapshot => {
          if (!isMounted.current) return;
          const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
          setUploadProgress(Math.round(progress));
        }, error => {
          reject(error);
        }, () => {
          resolve();
        });
      });
      if (!isMounted.current) return;
      // 3. Get download URL
      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
      if (!isMounted.current) return;
      // 4. Update Firestore
      const userDocRef = doc(db, 'users', uid);
      await updateDoc(userDocRef, {
        profileImageUrl: downloadURL,
        profileImageUpdatedAt: new Date(),
        updatedAt: new Date()
      });
      if (!isMounted.current) return;
      // 5. Sync to teacher student subcollection docs (non-blocking)
      syncToTeacherStudentDocs(downloadURL);
      // 6. Success
      onPhotoUpdated(downloadURL);
      toast.success('Profile photo updated successfully');
    } catch (error: any) {
      console.error('Error uploading profile photo:', error);
      if (error.code === 'storage/unauthorized') {
        toast.error('Upload failed: Permission denied');
      } else {
        toast.error('Failed to upload profile photo. Please try again.');
      }
    } finally {
      if (isMounted.current) {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    await uploadFile(file);
  };
  const handleDeletePhoto = async () => {
    if (!currentPhotoUrl || isDeleting) return;
    setIsDeleting(true);
    try {
      const userDocRef = doc(db, 'users', uid);
      await updateDoc(userDocRef, {
        profileImageUrl: null,
        profileImageUpdatedAt: new Date(),
        updatedAt: new Date()
      });
      if (!isMounted.current) return;
      // Sync removal to teacher student subcollection docs (non-blocking)
      syncToTeacherStudentDocs(null);
      onPhotoUpdated(null);
      toast.success('Profile photo removed');
    } catch (error) {
      console.error('Error deleting profile photo:', error);
      toast.error('Failed to remove profile photo');
    } finally {
      if (isMounted.current) {
        setIsDeleting(false);
      }
    }
  };
  // Avatar click handler
  // - If a photo exists → open popover menu
  // - If no photo → open file picker directly (no menu needed)
  const handleAvatarClick = () => {
    if (isUploading || isDeleting) return;
    if (currentPhotoUrl) {
      setShowPhotoMenu(prev => !prev);
    } else {
      fileInputRef.current?.click();
    }
  };
  const handleReplacePhoto = () => {
    setShowPhotoMenu(false);
    fileInputRef.current?.click();
  };
  const handleRemovePhotoFromMenu = () => {
    setShowPhotoMenu(false);
    handleDeletePhoto();
  };
  // Drag-and-drop handlers (only meaningful when no photo exists)
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentPhotoUrl && !isUploading && !isDeleting) {
      setIsDraggingPhoto(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(false);
    if (currentPhotoUrl) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    uploadFile(file);
  };
  // Brand ring uses green as the student-side primary accent.
  // Ring classes are applied inline (static strings) below so Tailwind can detect them at build time.
  return <div className="flex flex-col items-center text-center" data-id="element-11626">
      {/* Avatar Container — relative for popover anchoring */}
      <div className="relative" data-id="element-11627">
        <div onClick={handleAvatarClick} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} role="button" tabIndex={0} aria-label={currentPhotoUrl ? 'Open profile photo options' : 'Upload profile photo'} aria-haspopup={currentPhotoUrl ? 'menu' : undefined} aria-expanded={currentPhotoUrl ? showPhotoMenu : undefined} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleAvatarClick();
        }
        if (e.key === 'Escape' && showPhotoMenu) {
          setShowPhotoMenu(false);
        }
      }} className={`group relative w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden border-4 flex items-center justify-center cursor-pointer transition-all duration-200 ${isDark ? 'border-navy-800 bg-navy-700' : 'border-white bg-gray-100 shadow-sm'} ${!isUploading && !isDeleting ? isDark ? 'hover:ring-2 hover:ring-green-400/60' : 'hover:ring-2 hover:ring-green-500/50' : ''} ${isDraggingPhoto && !currentPhotoUrl || showPhotoMenu ? isDark ? 'ring-2 ring-green-400/60' : 'ring-2 ring-green-500/50' : ''}`} data-id="element-11628">
          {isUploading ? <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-11629">
              <LoaderIcon className="w-7 h-7 text-white animate-spin mb-1" data-id="element-11630" />
              <span className="text-white text-xs font-medium" data-id="element-11631">
                {uploadProgress}%
              </span>
            </div> : isDeleting ? <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-11632">
              <LoaderIcon className="w-7 h-7 text-white animate-spin mb-1" data-id="element-11633" />
              <span className="text-white text-xs font-medium" data-id="element-11634">Removing…</span>
            </div> : currentPhotoUrl ? <>
              <img src={currentPhotoUrl} alt={`${firstName} ${lastName}`} className="w-full h-full object-cover" onError={e => {
            e.currentTarget.style.display = 'none';
          }} data-id="element-11635" />
              {/* Hover overlay: dark veil + camera icon + caption.
               Always visible on touch devices (no hover); revealed on hover on desktop. */}
              <div className="absolute inset-0 rounded-full bg-black/45 flex flex-col items-center justify-center gap-1 transition-opacity duration-200 opacity-100 md:opacity-0 md:group-hover:opacity-100 pointer-events-none" data-id="element-11636">
                <CameraIcon className="w-7 h-7 text-white" strokeWidth={1.75} data-id="element-11637" />
                <span className="text-white text-xs font-medium tracking-wide" data-id="element-11638">
                  Change photo
                </span>
              </div>
            </> : <>
              <span className={`text-3xl sm:text-4xl font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'} ${isDraggingPhoto ? 'opacity-30' : ''}`} data-id="element-11639">
                {getInitials()}
              </span>
              {/* Empty-state hover overlay: prompt to upload */}
              <div className="absolute inset-0 rounded-full bg-black/40 flex flex-col items-center justify-center gap-1 transition-opacity duration-200 opacity-0 md:group-hover:opacity-100 pointer-events-none" data-id="element-11640">
                <CameraIcon className="w-7 h-7 text-white" strokeWidth={1.75} data-id="element-11641" />
                <span className="text-white text-xs font-medium tracking-wide" data-id="element-11642">
                  Upload photo
                </span>
              </div>
              {isDraggingPhoto && <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/30" data-id="element-11643">
                  <span className="text-xs font-medium text-white" data-id="element-11644">
                    Drop to upload
                  </span>
                </div>}
            </>}
        </div>

        {/* Action menu / popover — anchored beneath the avatar */}
        {showPhotoMenu && currentPhotoUrl && !isUploading && !isDeleting && <>
            {/* Click-outside backdrop */}
            <div className="fixed inset-0 z-40" onClick={() => setShowPhotoMenu(false)} aria-hidden="true" data-id="element-11645" />
            <div role="menu" aria-label="Profile photo actions" className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 w-48 rounded-xl shadow-lg z-50 overflow-hidden ${isDark ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-200'}`} data-id="element-11646">
              <div className="py-1.5" data-id="element-11647">
                <button type="button" role="menuitem" onClick={handleReplacePhoto} className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'text-gray-200 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-11648">
                  <UploadIcon className="h-4 w-4" data-id="element-11649" />
                  Replace Photo
                </button>
                <button type="button" role="menuitem" onClick={handleRemovePhotoFromMenu} className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'text-red-400 hover:bg-navy-700' : 'text-red-600 hover:bg-red-50'}`} data-id="element-11650">
                  <Trash2Icon className="h-4 w-4" data-id="element-11651" />
                  Remove Photo
                </button>
              </div>
            </div>
          </>}

        {/* Hidden file input */}
        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/jpeg, image/png, image/webp" className="hidden" data-id="element-11652" />
      </div>

      {/* Help text — beneath the avatar */}
      <p className={`mt-4 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11653">
        Accepted formats: JPG, PNG, WebP. Max size: 5MB.
      </p>
    </div>;
};